import React, { useCallback, useContext, useMemo } from 'react'
import { Button, BUTTON_SIZE, BUTTON_TYPE, NaverMap, MapMarker, MarkerType } from '~/app/components'
import ItemView from './ItemView'
import BorderBox from '~/app/components/order-list/common/BorderBox'
import { isNil } from 'ramda'
import styled from '~/app/components/styled'
import { GlobalContext } from '~/app/components/common/GlobalProvider'
import {
  ItemContainer,
  Title,
  Detail,
} from '~/app/components/order-list/common/OrderDetailsCommonStyled'
import { Typography } from '@yanolja-rnd/ya-com-core'

/**
 * HostInformation component
 * author: soojin.oh
 */

const PhoneLink = styled.a`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    ${Typography('body1').regular};
    color: ${({ theme }) => theme.color.yablue};
    text-decoration: none;
  }
`

const CUSTOMER_SERVICE_TEXT = ['• 고객 취소 및 상담 문의', '• 간단한 무대 시설', '• 어메니티 문의']
const USE_SERVICE_TEXT = [
  '• 상세 길찾기 문의',
  '• 초과 인원 사전 안내',
  '• 22시간 이후 입실 문의',
  '• 부대 시설 이용 사전 예약',
  '• 픽업 가능 여부 확인 후 사전 예약',
]

// type definitions
type HostInformationProps = {
  id?: string
  hostName?: string | null
  phone?: string | null
  customerServicePhone?: string | null
  address?: string
  coordinate?: {
    lat?: number
    lng?: number
  }
  markerType: MarkerType
  title?: string
}

// HostInformation component
const HostInformation: React.FunctionComponent<HostInformationProps> = function ({
  id,
  hostName,
  phone,
  customerServicePhone,
  address,
  coordinate,
  markerType,
  title,
}) {
  const { device } = useContext(GlobalContext)

  const markers = useMemo(
    () => [<MapMarker type={markerType} lat={coordinate?.lat} lng={coordinate?.lng} key={0} />],
    [coordinate]
  )

  const toPhoneLink = useCallback(
    phoneNumber => {
      return device ? (
        <PhoneLink href={`tel:${phoneNumber}`}>{phoneNumber}</PhoneLink>
      ) : (
        <div className={'description'}>{phoneNumber}</div>
      )
    },
    [device]
  )

  return (
    <ItemContainer id={id}>
      <Title>{`${title ?? '업체정보'}`}</Title>
      <ItemView title={'업체이름'} description={hostName} />
      {isNil(customerServicePhone) ? (
        <ItemView title={'전화번호'}>{toPhoneLink(phone)}</ItemView>
      ) : (
        <>
          <ItemView title={'고객 상담'}>{toPhoneLink(customerServicePhone)}</ItemView>
          <BorderBox text={CUSTOMER_SERVICE_TEXT} />
          <ItemView title={'이용 상담'}>{toPhoneLink(phone)}</ItemView>
          <BorderBox text={USE_SERVICE_TEXT} />
        </>
      )}
      <ItemView title={'업체주소'} description={address}>
        <Button
          type={BUTTON_TYPE.BLUE}
          size={BUTTON_SIZE.M}
          title={'주소복사'}
          className={'link'}
          dataClipboardText={address}
          style={{ border: 'none', borderRadius: 0, marginTop: '-1rem', padding: '1rem 0' }}
        >
          주소복사
        </Button>
      </ItemView>
      <Detail>
        {coordinate?.lat && coordinate?.lng && (
          <div className={'map-frame'}>
            <NaverMap
              lat={coordinate?.lat}
              lng={coordinate?.lng}
              zoom={15}
              markers={markers}
              id={`naver-map-${id}`}
            />
          </div>
        )}
      </Detail>
    </ItemContainer>
  )
}

export default HostInformation
